import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Home, Compass, Trophy, Activity, Users, User, Bell, LogOut, LifeBuoy } from "lucide-react";

const TABS = [
  { to: "/app", label: "Home", icon: Home, exact: true },
  { to: "/app/discover", label: "Discover", icon: Compass },
  { to: "/app/play", label: "Play", icon: Trophy },
  { to: "/app/performance", label: "Performance", icon: Activity },
  { to: "/app/community", label: "Community", icon: Users },
  { to: "/app/profile", label: "Profile", icon: User },
];

export default function MainLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const i = setInterval(loadNotifs, 30000); return () => clearInterval(i); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const isActive = (t) =>
    t.exact ? location.pathname === t.to : location.pathname.startsWith(t.to);

  return (
    <div className="min-h-screen bg-background">
      <header className="glass sticky top-0 z-40 border-b border-border">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/app" className="flex items-center gap-2" data-testid="app-logo-home">
            <img src="/spora1-logo.webp" alt="SPORA1" className="h-9 w-auto" />
          </Link>

          <nav className="no-scrollbar hidden items-center gap-1 overflow-x-auto md:flex">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = isActive(t);
              return (
                <Link key={t.to} to={t.to} data-testid={`tab-${t.label.toLowerCase()}`}
                  className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-all lg:px-4 ${
                    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                  <Icon style={{ width: 17, height: 17 }} /> <span className="hidden lg:inline">{t.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="relative flex items-center gap-2">
            <Link to="/help" data-testid="help-btn" title="Help & Support"
              className="rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground">
              <LifeBuoy className="h-5 w-5" />
            </Link>
            <button data-testid="notif-btn"
              onClick={() => { setShowNotif((s) => !s); if (!showNotif && unread) api.post("/notifications/read").then(loadNotifs); }}
              className="relative rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground">
              <Bell className="h-5 w-5" />
              {unread > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" />}
            </button>
            {showNotif && (
              <div className="absolute right-0 top-12 z-50 w-80 rounded-xl border border-border bg-popover p-2 shadow-2xl">
                <p className="px-2 py-1 text-xs font-mono uppercase text-muted-foreground">Notifications</p>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && <p className="px-2 py-4 text-sm text-muted-foreground">No notifications yet</p>}
                  {notifs.map((n) => (
                    <div key={n.id} className="rounded-lg px-2 py-2 hover:bg-accent">
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs text-muted-foreground">{n.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
              className="rounded-lg p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-6 lg:pb-10 lg:pt-8">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="glass fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 border-t border-border md:hidden">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = isActive(t);
          return (
            <Link key={t.to} to={t.to} data-testid={`mtab-${t.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${active ? "text-primary" : "text-muted-foreground"}`}>
              <Icon style={{ width: 20, height: 20 }} /> {t.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
